import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Search, Clock, Lock } from 'lucide-react';
import { useSteamUser } from '../hooks/useSteamUser';
import { UserProfileHeader } from '../components/UserProfileHeader';

type TabType = 'completed' | 'incomplete';

export function GameAchievementsPage() {
  const { username, appid } = useParams<{ username: string; appid: string }>();
  const navigate = useNavigate();
  const { user, gameStats, isLoading, error } = useSteamUser(username || '');
  const [activeTab, setActiveTab] = useState<TabType>('completed');
  const [searchQuery, setSearchQuery] = useState('');

  const game = gameStats?.achievements.find(g => g.appid === parseInt(appid || '0'));

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const completedAchievements = game?.achievements.filter(achievement => 
    (achievement.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    achievement.description.toLowerCase().includes(searchQuery.toLowerCase())) &&
    achievement.achieved === 1
  ) || [];

  const incompleteAchievements = game?.achievements.filter(achievement =>
    (achievement.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    achievement.description.toLowerCase().includes(searchQuery.toLowerCase())) &&
    achievement.achieved === 0
  ) || [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-32 bg-gray-200 rounded-lg mb-6"></div>
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !user || !gameStats || !game) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error || 'Failed to load achievements'}</p>
          <button
            onClick={() => navigate(`/user/${username}`)}
            className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Return to Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={() => navigate(`/user/${username}/achievements`)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Achievements
        </button>

        <UserProfileHeader user={user} gameStats={gameStats} />

        <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {game.name} Achievements
              </h1>
              <p className="text-gray-600">
                {game.unlockedAchievements} of {game.totalAchievements} achievements unlocked
                ({((game.unlockedAchievements / game.totalAchievements) * 100).toFixed(1)}%)
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search achievements..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:w-auto pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setActiveTab('completed')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'completed'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Completed ({completedAchievements.length})
            </button>
            <button
              onClick={() => setActiveTab('incomplete')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'incomplete'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Incomplete ({incompleteAchievements.length})
            </button>
          </div>

          <div className="space-y-4">
            {activeTab === 'completed' ? (
              completedAchievements.length > 0 ? (
                completedAchievements.map((achievement) => (
                  <div
                    key={achievement.apiname}
                    className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {achievement.icon ? (
                      <img
                        src={achievement.icon}
                        alt={achievement.name}
                        className="w-12 h-12 rounded"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-indigo-100 rounded flex items-center justify-center">
                        <Trophy className="w-6 h-6 text-indigo-600" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{achievement.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{achievement.description}</p>
                      <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                        <Clock className="w-4 h-4" />
                        <span>Unlocked on {formatDate(achievement.unlocktime)}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No achievements found</p>
                </div>
              )
            ) : incompleteAchievements.length > 0 ? (
              incompleteAchievements.map((achievement) => (
                <div
                  key={achievement.apiname}
                  className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg"
                >
                  {achievement.icongray ? (
                    <img
                      src={achievement.icongray}
                      alt={achievement.name}
                      className="w-12 h-12 rounded opacity-50 grayscale"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                      <Lock className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-600">{achievement.name}</h3>
                      {achievement.hidden === 1 && (
                        <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
                          Hidden
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {achievement.hidden === 1 && !searchQuery
                        ? "This achievement is hidden"
                        : achievement.description}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No incomplete achievements found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}